import type { DayId } from '../core/state';
import { OBJECTS, festivalNow, type MapDef, type MapId } from '../world/map';
import type { Decor, ObjectId, Rect } from '../world/mapdef';
import { OUTLINE, block, ellipse, rect, shade, shadow, type Ctx } from './paint';
import { drawTree, wind } from './nature';
import { hash2 } from './noise';
import { OBJ2, drawDecor2 } from './props2';
import { ANIMATED_EXTRA, drawExtraDecor, extraObjDrawer } from './propRegistry';
import { ANIMATED3_DECOR, ANIMATED3_OBJ, OBJ3, OVERHEAD_DECOR, drawDecor3 } from './props3';
import { ANIMATED4_DECOR, ANIMATED4_OBJ, OBJ4, OVERHEAD4_DECOR, drawDecor4 } from './props4';

/** A world prop drawn every frame, depth-sorted against characters by `sortY`. */
export interface PropSprite {
  sortY: number;
  /** Bounding box for culling. */
  x0: number;
  x1: number;
  top: number;
  bottom: number;
  days?: DayId[];
  /** Optional per-frame visibility (quest items...). */
  visible?: () => boolean;
  draw(c: Ctx, t: number): void;
}

type Drawer = (c: Ctx, r: Rect, t: number) => void;

// ---------------------------------------------------------------------------
// Interactive objects
// ---------------------------------------------------------------------------

const OBJ_BASE: Partial<Record<ObjectId, Drawer>> = {
  shelfL: shelf,
  shelfR: shelf,
  desk(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 4);
    block(c, r.x + 26, r.y - 18, 16, 14, '#3d5a80', '#4f6f98', '#2c4462');
    block(c, r.x, r.y - 6, r.w, r.h + 6, '#8a5530', '#a36a40', '#6b3f22');
    rect(c, '#6b3f22', r.x + 3, r.y + r.h - 2, 4, 2);
    rect(c, '#6b3f22', r.x + r.w - 7, r.y + r.h - 2, 4, 2);
    block(c, r.x + 30, r.y - 24, 24, 18, '#2b3440');
    rect(c, '#58b6f0', r.x + 32, r.y - 22, 20, 12);
    rect(c, '#a8dcff', r.x + 34, r.y - 20, 10, 2);
    rect(c, '#ffffff', r.x + 34, r.y - 16, 14, 1);
    rect(c, '#ffffff', r.x + 34, r.y - 13, 10, 1);
    rect(c, '#2b3440', r.x + 40, r.y - 6, 4, 2);
    block(c, r.x + 5, r.y - 4, 14, 8, '#fffdf6');
    rect(c, '#9aa', r.x + 8, r.y - 2, 8, 1);
    block(c, r.x + 21, r.y - 6, 5, 6, '#e2493b');
  },
  plant(c, r, t) {
    pottedPlant(c, r.x + r.w / 2, r.y + r.h, t);
  },
  tea(c, r) {
    counter(c, r, '#7a4a2a', '#94603a', '#5e381f');
    block(c, r.x + 4, r.y - 24, 12, 12, '#c9d2d8', '#eef3f6', '#a2adb5');
    rect(c, '#3a4650', r.x + 8, r.y - 27, 5, 3);
    rect(c, '#a2adb5', r.x + 16, r.y - 20, 4, 2);
    block(c, r.x + 22, r.y - 16, 6, 6, '#ffffff');
    block(c, r.x + 29, r.y - 16, 6, 6, '#f4c27a');
    block(c, r.x + 36, r.y - 22, 8, 12, '#c97a3a', '#e09a5a');
  },
  water(c, r, t) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 3);
    block(c, r.x, r.y - 22, r.w, r.h + 22, '#e9eef2', '#ffffff', '#c3ccd3');
    block(c, r.x + 2, r.y - 40, r.w - 4, 20, '#7fc8f0', '#bfe6ff', '#5aa8d8');
    rect(c, '#bfe6ff', r.x + 3, r.y - 34 + Math.round(Math.sin(t * 2)), r.w - 6, 1);
    rect(c, 'rgba(255,255,255,0.7)', r.x + 4, r.y - 38, 2, 14);
    rect(c, '#e2493b', r.x + 3, r.y - 14, 3, 3);
    rect(c, '#3a8fd9', r.x + r.w - 6, r.y - 14, 3, 3);
  },
  sofa(c, r) {
    sofa(c, r, '#b07a52', '#c99467', '#8a5c3a', '#f1e2c8');
  },
  toilet(c, r) {
    // Row of stall doors.
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2, 3);
    for (let i = 0; i < 3; i++) {
      const x = r.x + i * (r.w / 3);
      block(c, x, r.y - 22, r.w / 3 - 1, r.h + 22, '#5b8fb0', '#78aacb', '#456f8a');
      rect(c, '#ffd34e', x + r.w / 3 - 6, r.y - 6, 2, 3);
      rect(c, i === 1 ? '#e2493b' : '#3fd38a', x + 3, r.y - 18, 4, 3);
    }
    block(c, r.x + r.w / 2 - 8, r.y - 34, 16, 10, '#ffffff');
    rect(c, '#3a8fd9', r.x + r.w / 2 - 6, r.y - 32, 4, 6);
    rect(c, '#e2493b', r.x + r.w / 2 + 2, r.y - 32, 4, 6);
  },
  easel(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, 14, 3);
    rect(c, OUTLINE, r.x + 6, r.y - 26, 3, r.h + 26);
    rect(c, OUTLINE, r.x + r.w - 9, r.y - 26, 3, r.h + 26);
    block(c, r.x, r.y - 36, r.w, 24, '#fff9ea');
    rect(c, '#9fd6ff', r.x + 2, r.y - 34, r.w - 4, 10);
    ellipse(c, '#ffd34e', r.x + 22, r.y - 30, 3, 3);
    rect(c, '#57c278', r.x + 2, r.y - 24, r.w - 4, 4);
    rect(c, '#e2493b', r.x + 6, r.y - 28, 5, 5);
    block(c, r.x + 3, r.y - 12, r.w - 6, 3, '#8a5530');
    ellipse(c, OUTLINE, r.x + r.w + 5, r.y + 3, 7, 4);
    ellipse(c, '#e8c89a', r.x + r.w + 5, r.y + 3, 6, 3);
    rect(c, '#e2493b', r.x + r.w + 1, r.y + 1, 2, 2);
    rect(c, '#3a8fd9', r.x + r.w + 5, r.y + 1, 2, 2);
  },
  chineseDesk(c, r) {
    studentDesk(c, r.x + r.w / 2, r.y + r.h, 1);
    block(c, r.x + 4, r.y - 13, 12, 8, '#fffdf6');
    rect(c, '#2a1810', r.x + 7, r.y - 11, 1, 5);
    rect(c, '#2a1810', r.x + 10, r.y - 11, 1, 5);
    rect(c, '#c9a36a', r.x + 19, r.y - 15, 2, 8);
  },
  atlasDesk(c, r) {
    studentDesk(c, r.x + r.w / 2, r.y + r.h, 2);
  },
  libShelf(c, r) {
    bookshelf(c, r.x + r.w / 2, r.y + r.h, r.w, 1);
    block(c, r.x + r.w - 10, r.y - 34, 8, 6, '#ffd34e');
  },
  board(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2, 3);
    rect(c, OUTLINE, r.x + 5, r.y - 6, 3, r.h + 6);
    rect(c, OUTLINE, r.x + r.w - 8, r.y - 6, 3, r.h + 6);
    block(c, r.x, r.y - 34, r.w, 30, '#b8814e');
    rect(c, '#d9a876', r.x + 2, r.y - 32, r.w - 4, 26);
    ['#fff6a8', '#ffc2d6', '#bfe6ff', '#c9f2c0'].forEach((col, i) => {
      block(c, r.x + 4 + i * 10, r.y - 30 + (i % 2) * 4, 8, 9, col);
      rect(c, '#e2493b', r.x + 7 + i * 10, r.y - 30 + (i % 2) * 4, 2, 2);
    });
  },
  vending(c, r, t) {
    shadow(c, r.x + r.w / 2, r.y + r.h, 9, 3);
    block(c, r.x - 2, r.y - 30, r.w + 4, r.h + 30, '#d0453a', '#e8685c', '#a8261c');
    rect(c, '#1b2430', r.x, r.y - 26, r.w, 16);
    for (let i = 0; i < 3; i++) for (let j = 0; j < 2; j++) rect(c, ['#3fd38a', '#ffd34e', '#5a8dff'][i], r.x + 2 + i * 4, r.y - 24 + j * 7, 2, 5);
    rect(c, `rgba(255,255,255,${0.15 + 0.1 * Math.sin(t * 3)})`, r.x, r.y - 26, r.w, 2);
    rect(c, '#2a1810', r.x + 2, r.y - 6, r.w - 4, 3);
  },
  canteen(c, r) {
    counter(c, r, '#c9d2d8', '#eef3f6', '#a2adb5');
    for (let i = 0; i < Math.floor((r.w - 4) / 18); i++) {
      block(c, r.x + 4 + i * 18, r.y - 18, 15, 8, '#9fb0b8');
      rect(c, ['#ffd34e', '#57c278', '#e8735a', '#f6efe2'][i % 4], r.x + 6 + i * 18, r.y - 16, 11, 4);
    }
    rect(c, 'rgba(255,255,255,0.4)', r.x + 4, r.y - 30, r.w - 8, 2);
  },
  piano(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 3, 4);
    block(c, r.x, r.y - 20, r.w, r.h + 20, '#1c1a20', '#3a3640', '#0e0d10');
    block(c, r.x + 3, r.y - 2, r.w - 6, 8, '#fbf8ef');
    for (let k = r.x + 5; k < r.x + r.w - 5; k += 4) rect(c, '#b9b4a8', k, r.y - 1, 1, 6);
    for (let k = r.x + 6; k < r.x + r.w - 6; k += 8) rect(c, '#1c1a20', k, r.y - 1, 3, 3);
    block(c, r.x + 20, r.y - 30, 22, 11, '#fffdf6');
    rect(c, '#2a1810', r.x + 23, r.y - 26, 16, 1);
    rect(c, '#2a1810', r.x + 23, r.y - 23, 16, 1);
  },
  benchL: bench,
  benchR: bench,
  picnic(c, r, t) {
    for (let y = 0; y < r.h; y += 8) for (let x = 0; x < r.w; x += 8) rect(c, ((x + y) / 8) % 2 ? '#f4f1ea' : '#e2493b', r.x + x, r.y + y, 8, 8);
    c.strokeStyle = 'rgba(0,0,0,0.25)';
    c.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);
    block(c, r.x + 6, r.y + 5, 16, 11, '#c98b4a', '#e0a86a', '#9a6432');
    rect(c, '#8a5530', r.x + 10, r.y + 2, 8, 2);
    block(c, r.x + 44, r.y + 9, 7, 11, '#ffb347', '#ffd08a');
    block(c, r.x + 28, r.y + 18, 14, 9, '#3d5a80');
    ellipse(c, '#1b2430', r.x + 33, r.y + 22, 2.5, 2.5);
    const n = (t * 1.2) % 1;
    c.globalAlpha = 1 - n;
    c.fillStyle = '#ffd34e';
    c.font = '8px sans-serif';
    c.fillText('♪', r.x + 40 + n * 6, r.y + 14 - n * 14);
    c.globalAlpha = 1;
  },
  field(c, r, t) {
    block(c, r.x + 4, r.y + 14, 12, 9, '#a8693a', '#c98b4a');
    block(c, r.x + r.w - 16, r.y + 8, 12, 13, '#7f8c8d', '#9fb0b1');
    for (let i = 0; i < 3; i++) ellipse(c, '#fbf8ef', r.x + r.w - 12 + i * 4, r.y + 8 + Math.sin(t * 3 + i), 2, 2);
  },
  busStop(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 3, 3);
    rect(c, OUTLINE, r.x + 2, r.y - 30, 3, r.h + 30);
    rect(c, OUTLINE, r.x + r.w - 5, r.y - 30, 3, r.h + 30);
    block(c, r.x - 2, r.y - 34, r.w + 4, 6, '#2f7fc1', '#4f9fe1', '#23679e');
    block(c, r.x + 4, r.y - 26, r.w - 8, 14, 'rgba(190,230,255,0.55)');
    block(c, r.x + 6, r.y - 6, r.w - 12, 4, '#8a5530', '#a36a40');
    block(c, r.x + r.w - 2, r.y - 44, 10, 10, '#ffd34e');
    rect(c, '#2f7fc1', r.x + r.w + 1, r.y - 41, 4, 4);
  },
  mailbox(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, 6, 2);
    rect(c, OUTLINE, r.x + r.w / 2 - 1, r.y - 10, 3, r.h + 10);
    block(c, r.x - 1, r.y - 20, r.w + 2, 11, '#d0453a', '#e8685c', '#a8261c');
    rect(c, '#2a1810', r.x + 1, r.y - 16, r.w - 2, 1);
    rect(c, '#ffd34e', r.x + r.w, r.y - 22, 2, 6);
  },
  pond(c, r, t) {
    // Ripples and the occasional fish (the water itself is in the tile layer).
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.4 + i / 3) % 1;
      const cx = r.x + 40 + i * 52, cy = r.y + 28 + (i % 2) * 18;
      c.strokeStyle = `rgba(255,255,255,${0.5 * (1 - k)})`;
      c.lineWidth = 1;
      c.beginPath();
      c.ellipse(cx, cy, 3 + k * 10, 1.5 + k * 4, 0, 0, Math.PI * 2);
      c.stroke();
    }
    const f = (t * 0.25) % 1;
    const fx = r.x + 30 + f * (r.w - 60), fy = r.y + 40 + Math.sin(t * 2) * 6;
    ellipse(c, 'rgba(255,150,60,0.75)', fx, fy, 4, 2);
    rect(c, 'rgba(255,150,60,0.75)', fx - 6, fy - 1, 2, 2);
  },
  bush1: berryBush,
  bush2: berryBush,
  bush3: berryBush,
  kiteTree(c, r, t) {
    tree(c, r.x + r.w / 2, r.y + r.h, t, 'tree', 1.25);
  },
  swing(c, r, t) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 3);
    rect(c, OUTLINE, r.x, r.y - 30, 3, r.h + 30);
    rect(c, OUTLINE, r.x + r.w - 3, r.y - 30, 3, r.h + 30);
    block(c, r.x - 2, r.y - 33, r.w + 4, 4, '#e2493b', '#f06a5a');
    const sw = Math.sin(t * 1.6) * 3;
    rect(c, '#9aa6ae', r.x + 9, r.y - 29, 1, 22);
    rect(c, '#9aa6ae', r.x + 19, r.y - 29, 1, 22);
    block(c, r.x + 7 + sw, r.y - 8, 15, 3, '#3a8fd9');
  },
  shoes(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 3);
    block(c, r.x, r.y - 12, r.w, r.h + 12, '#8a5530', '#a36a40', '#6b3f22');
    rect(c, '#6b3f22', r.x + 2, r.y - 4, r.w - 4, 1);
    block(c, r.x + 3, r.y - 10, 8, 5, '#e2493b');
    block(c, r.x + 12, r.y - 10, 8, 5, '#3a8fd9');
    block(c, r.x + 4, r.y - 1, 8, 4, '#2a1810');
    block(c, r.x + 16, r.y - 1, 8, 4, '#ffd34e');
  },
  sink(c, r) {
    counter(c, r, '#e8e2d6', '#ffffff', '#c9c1b2');
    block(c, r.x + 4, r.y - 8, r.w - 8, 10, '#9fb0b8', '#c5d3da');
    rect(c, '#7fc8f0', r.x + 6, r.y - 6, r.w - 12, 6);
    rect(c, '#c5d3da', r.x + 13, r.y - 18, 3, 10);
    rect(c, '#c5d3da', r.x + 13, r.y - 18, 7, 2);
  },
  oven(c, r) {
    counter(c, r, '#e8e2d6', '#ffffff', '#c9c1b2');
    block(c, r.x + 3, r.y - 4, r.w - 6, r.h + 2, '#3c4652', '#56616e');
    block(c, r.x + 6, r.y, r.w - 12, 10, '#ff9f43');
    rect(c, '#ffd08a', r.x + 8, r.y + 2, r.w - 16, 2);
    for (let i = 0; i < 2; i++) block(c, r.x + 6 + i * 12, r.y - 14, 8, 6, '#c9d2d8');
  },
  fridge(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, 8, 3);
    block(c, r.x - 1, r.y - 30, r.w + 2, r.h + 30, '#eef3f6', '#ffffff', '#c3ccd3');
    rect(c, '#c3ccd3', r.x, r.y - 14, r.w, 1);
    rect(c, '#9aa6ae', r.x + r.w - 4, r.y - 26, 2, 8);
    rect(c, '#9aa6ae', r.x + r.w - 4, r.y - 10, 2, 6);
    block(c, r.x + 2, r.y - 26, 5, 5, '#ffd34e');
  },
  tv(c, r, t) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 3);
    block(c, r.x, r.y - 6, r.w, r.h + 6, '#5e381f', '#7a4a2a');
    block(c, r.x + 2, r.y - 26, r.w - 4, 20, '#1b1d24');
    const k = Math.floor(t * 2) % 3;
    rect(c, ['#4f9fe1', '#57c278', '#ff9f43'][k], r.x + 4, r.y - 24, r.w - 8, 15);
    rect(c, 'rgba(255,255,255,0.3)', r.x + 4, r.y - 24, r.w - 8, 2);
  },
  homeDesk(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 4);
    block(c, r.x + 16, r.y - 14, 14, 12, '#e2493b', '#f06a5a', '#b83a2e');
    block(c, r.x, r.y - 6, r.w, r.h + 6, '#e3c08f', '#f2d6aa', '#c09a68');
    block(c, r.x + 4, r.y - 4, 14, 8, '#fffdf6');
    rect(c, '#57c278', r.x + 20, r.y - 4, 5, 8);
    rect(c, '#3a8fd9', r.x + 26, r.y - 4, 5, 8);
    rect(c, '#2a1810', r.x + r.w - 9, r.y - 22, 2, 18);
    block(c, r.x + r.w - 14, r.y - 26, 11, 6, '#ffd34e', '#fff0a0');
  },
  bed(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 3, 4);
    block(c, r.x, r.y - 10, r.w, r.h + 10, '#8a5530', '#a36a40', '#6b3f22');
    block(c, r.x + 2, r.y - 6, r.w - 4, r.h + 4, '#f4f1ea');
    block(c, r.x + 5, r.y - 4, r.w - 10, 9, '#ffffff', undefined, '#dcd6cc');
    block(c, r.x + 2, r.y + 8, r.w - 4, r.h - 9, '#5c84c8', '#7aa0e0', '#3f63a0');
    for (let i = 0; i < 3; i++) rect(c, '#ffd34e', r.x + 7 + i * 8, r.y + 14 + (i % 2) * 6, 2, 2);
  },
  bath(c, r) {
    shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 4);
    block(c, r.x, r.y - 8, r.w, r.h + 8, '#ffffff', '#ffffff', '#cfd8de');
    rect(c, '#a9dcf5', r.x + 3, r.y - 5, r.w - 6, r.h - 1);
    rect(c, '#d6f0fc', r.x + 5, r.y - 4, 8, 2);
    block(c, r.x + r.w - 12, r.y - 3, 6, 4, '#ffd34e');
  },
  shopCounter(c, r) {
    counter(c, r, '#3fa36f', '#5fc38f', '#2a7a50');
    block(c, r.x + 6, r.y - 18, 16, 10, '#2b3440');
    rect(c, '#57c278', r.x + 8, r.y - 16, 12, 4);
    block(c, r.x + 34, r.y - 16, 22, 8, '#ffd34e', '#fff0a0');
    for (let i = 0; i < 3; i++) block(c, r.x + 36 + i * 7, r.y - 22, 5, 6, ['#e2493b', '#57c278', '#3a8fd9'][i]);
  },
};

// ---------------------------------------------------------------------------
// Decor
// ---------------------------------------------------------------------------

function drawDecor(c: Ctx, d: Decor, t: number): void {
  if (drawDecor2(c, d, t) || drawDecor3(c, d, t) || drawDecor4(c, d, t) || drawExtraDecor(c, d, t)) return;
  const { x, y } = d;
  switch (d.kind) {
    case 'tree':
    case 'cherry':
    case 'pine':
      return tree(c, x, y, t, d.kind);
    case 'bushDeco':
      return bushShape(c, x, y, t, '#3f9a4a');
    case 'flowerbed':
      block(c, x - 16, y - 10, 32, 10, '#6b4a2e', undefined, undefined, '#3e2a18');
      for (let i = 0; i < 6; i++) {
        rect(c, '#3f8a3a', x - 13 + i * 5, y - 8, 2, 5);
        rect(c, ['#ff6f91', '#ffd34e', '#ff9f43', '#c58cff'][i % 4], x - 14 + i * 5, y - 11 + (i % 2), 4, 3);
      }
      return;
    case 'lamp': {
      shadow(c, x, y, 4, 1.5);
      rect(c, OUTLINE, x - 1, y - 30, 3, 30);
      rect(c, '#5a6470', x, y - 29, 1, 28);
      block(c, x - 4, y - 34, 9, 5, '#3a4650');
      rect(c, '#fff3c0', x - 3, y - 30, 7, 1);
      return;
    }
    case 'hydrant':
      shadow(c, x, y, 4, 1.5);
      block(c, x - 3, y - 9, 6, 9, '#d0453a', '#e8685c');
      block(c, x - 4, y - 11, 8, 3, '#a8261c');
      rect(c, '#a8261c', x - 5, y - 6, 2, 2);
      return;
    case 'booth':
      shadow(c, x, y, 16, 3);
      block(c, x - 14, y - 26, 28, 26, '#e7dcc6', '#f6ecd8', '#c9bfa8');
      block(c, x - 16, y - 30, 32, 6, '#4f6fa0', '#7392c4', '#34507a');
      rect(c, '#9fd3f0', x - 10, y - 20, 20, 8);
      rect(c, 'rgba(255,255,255,0.6)', x - 8, y - 19, 3, 6);
      return;
    case 'signSchool':
      rect(c, OUTLINE, x - 1, y - 24, 3, 24);
      block(c, x - 18, y - 32, 36, 12, '#2f7a3a', '#3f9a4a');
      rect(c, '#fff6dc', x - 14, y - 28, 28, 4);
      return;
    case 'signStore':
      rect(c, OUTLINE, x, y - 18, 2, 18);
      block(c, x - 10, y - 28, 22, 12, '#3fa36f', '#5fc38f');
      rect(c, '#fff', x - 6, y - 24, 4, 4);
      rect(c, '#ffd34e', x, y - 24, 8, 4);
      return;
    case 'goal':
      rect(c, 'rgba(0,0,0,0.2)', x - 14, y - 2, 30, 3);
      c.strokeStyle = '#f4f1ea';
      c.lineWidth = 2;
      c.strokeRect(x - 12, y - 20, 26, 18);
      c.strokeStyle = 'rgba(255,255,255,0.35)';
      c.lineWidth = 0.5;
      for (let i = 0; i < 26; i += 4) {
        c.beginPath();
        c.moveTo(x - 12 + i, y - 20);
        c.lineTo(x - 12 + i, y - 2);
        c.stroke();
      }
      return;
    case 'hoop':
      shadow(c, x, y, 6, 2);
      rect(c, OUTLINE, x - 1, y - 40, 3, 40);
      block(c, x - 12, y - 52, 24, 16, '#ffffff', undefined, '#cfd8de');
      c.strokeStyle = '#e2493b';
      c.strokeRect(x - 5.5, y - 47.5, 11, 7);
      rect(c, '#ff7a2f', x - 7, y - 37, 14, 2);
      return;
    case 'slide':
      shadow(c, x, y, 16, 3);
      block(c, x - 14, y - 26, 8, 26, '#e2b33b', '#f2d06a');
      c.fillStyle = OUTLINE;
      c.beginPath();
      c.moveTo(x - 7, y - 27);
      c.lineTo(x + 16, y - 1);
      c.lineTo(x + 10, y + 1);
      c.lineTo(x - 7, y - 19);
      c.fill();
      c.fillStyle = '#3a8fd9';
      c.beginPath();
      c.moveTo(x - 6, y - 25);
      c.lineTo(x + 14, y - 2);
      c.lineTo(x + 10, y - 1);
      c.lineTo(x - 6, y - 20);
      c.fill();
      return;
    case 'armchairs':
      for (const dx of [-24, 16]) {
        shadow(c, x + dx + 4, y, 10, 3);
        block(c, x + dx - 6, y - 20, 20, 20, '#5e381f', '#7a4a2a', '#4a2a16');
        block(c, x + dx - 3, y - 12, 14, 9, '#f1e2c8', '#ffffff');
      }
      return;
    case 'teaTable':
      shadow(c, x, y, 14, 3);
      block(c, x - 13, y - 12, 26, 12, '#85532d', '#a36a40', '#6b3f22');
      block(c, x - 8, y - 15, 6, 5, '#ffffff');
      block(c, x + 2, y - 14, 6, 4, '#f4c27a');
      return;
    case 'pottedPlant':
      return pottedPlant(c, x, y, t);
    case 'lockers': {
      const w = d.w ?? 64;
      const n = Math.round(w / 16) * 1;
      for (let i = 0; i < n; i++) {
        const lx = x - w / 2 + i * (w / n);
        block(c, lx, y - 30, w / n, 30, ['#4f8fc0', '#57c278', '#e2b33b'][i % 3]);
        rect(c, 'rgba(0,0,0,0.25)', lx + 3, y - 26, w / n - 6, 1);
        rect(c, 'rgba(0,0,0,0.25)', lx + 3, y - 23, w / n - 6, 1);
        rect(c, '#2a1810', lx + w / n - 4, y - 16, 2, 5);
      }
      return;
    }
    case 'studentDesk':
      return studentDesk(c, x, y, 0);
    case 'teacherDesk':
      shadow(c, x, y, 38, 4);
      block(c, x - 38, y - 14, 76, 14, '#6b3f22', '#85532f', '#4a2a16');
      block(c, x - 30, y - 18, 14, 6, '#fffdf6');
      block(c, x + 14, y - 22, 8, 10, '#e2493b');
      ellipse(c, '#d0453a', x + 26, y - 18, 4, 4);
      rect(c, '#3f9a4a', x + 26, y - 24, 1, 3);
      return;
    case 'bookshelf':
      return bookshelf(c, x, y, d.w ?? 64, 0);
    case 'bookshelfHome':
      return bookshelf(c, x, y, 14, 2);
    case 'readingTable':
      shadow(c, x, y, 30, 4);
      block(c, x - 30, y - 12, 60, 12, '#8a5530', '#a36a40', '#6b3f22');
      block(c, x - 22, y - 15, 12, 6, '#5a8dff');
      block(c, x + 6, y - 15, 12, 6, '#e2b33b');
      rect(c, '#2a1810', x - 2, y - 26, 2, 12);
      block(c, x - 6, y - 30, 10, 5, '#3fd38a', '#7fe8b0');
      return;
    case 'cafeTable':
      shadow(c, x, y, 16, 4);
      block(c, x - 16, y - 12, 32, 12, '#e8e2d6', '#ffffff', '#c9c1b2');
      block(c, x - 10, y - 15, 8, 5, '#ffd34e');
      block(c, x + 3, y - 15, 8, 5, '#57c278');
      return;
    case 'shoeLockers': {
      block(c, x - 8, y - 28, 16, 28, '#8a5530', '#a36a40', '#6b3f22');
      for (let j = 0; j < 4; j++) rect(c, '#4a2a16', x - 6, y - 26 + j * 7, 12, 5);
      for (let j = 0; j < 4; j++) rect(c, ['#e2493b', '#f4f1ea', '#3a8fd9', '#2a1810'][j], x - 4, y - 24 + j * 7, 8, 2);
      return;
    }
    case 'sinkRow': {
      const w = d.w ?? 32;
      block(c, x - w / 2, y - 12, w, 12, '#ffffff', '#ffffff', '#cfd8de');
      for (let i = 0; i < Math.max(1, w / 16); i++) {
        ellipse(c, '#a9dcf5', x - w / 2 + 8 + i * 16, y - 7, 5, 3);
        rect(c, '#9aa6ae', x - w / 2 + 7 + i * 16, y - 16, 2, 6);
      }
      return;
    }
    case 'diningTable':
      shadow(c, x, y, 22, 4);
      for (const dx of [-18, 14]) block(c, x + dx, y - 16, 6, 14, '#6b3f22', '#85532f');
      block(c, x - 22, y - 14, 44, 14, '#b8814e', '#d9a876', '#8a5c34');
      block(c, x - 14, y - 18, 10, 6, '#ffffff');
      block(c, x + 4, y - 18, 10, 6, '#ffffff');
      ellipse(c, '#e8735a', x - 9, y - 15, 3, 2);
      ellipse(c, '#57c278', x + 9, y - 15, 3, 2);
      return;
    case 'livingSofa':
      return sofa(c, { x: x - 32, y: y - 10, w: 64, h: 10 }, '#6f8f5a', '#8aab72', '#506a40', '#f1e2c8');
    case 'rugRound':
      return;
    case 'wardrobe':
      shadow(c, x, y, 10, 3);
      block(c, x - 9, y - 40, 18, 40, '#8a5530', '#a36a40', '#6b3f22');
      rect(c, '#6b3f22', x, y - 38, 1, 36);
      rect(c, '#ffd34e', x - 3, y - 22, 2, 3);
      rect(c, '#ffd34e', x + 2, y - 22, 2, 3);
      return;
    case 'toyBox':
      shadow(c, x, y, 9, 3);
      block(c, x - 8, y - 12, 16, 12, '#ff9f43', '#ffc07a', '#d97a20');
      ellipse(c, '#3a8fd9', x - 3, y - 13, 3, 3);
      block(c, x + 1, y - 17, 5, 5, '#e2493b');
      return;
    case 'storeShelf': {
      const w = d.w ?? 64;
      shadow(c, x, y, w / 2, 3);
      block(c, x - w / 2, y - 26, w, 26, '#e8e2d6', '#ffffff', '#c9c1b2');
      for (let j = 0; j < 3; j++) {
        rect(c, '#9a8f7c', x - w / 2 + 2, y - 18 + j * 7, w - 4, 1);
        for (let i = 0; i < w - 8; i += 6) rect(c, ['#e2493b', '#ffd34e', '#57c278', '#5a8dff', '#ff9f43'][(i / 6 + j + (d.variant ?? 0)) % 5], x - w / 2 + 4 + i, y - 23 + j * 7, 4, 5);
      }
      return;
    }
    case 'storeFridge': {
      const w = d.w ?? 80;
      shadow(c, x, y, w / 2, 3);
      block(c, x - w / 2, y - 36, w, 36, '#c9d2d8', '#eef3f6', '#a2adb5');
      for (let i = 0; i < w - 8; i += 16) {
        rect(c, 'rgba(190,230,255,0.7)', x - w / 2 + 4 + i, y - 32, 14, 28);
        for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) rect(c, ['#e2493b', '#57c278', '#f4f1ea'][(k + j + i) % 3], x - w / 2 + 6 + i + k * 4, y - 30 + j * 9, 3, 6);
      }
      return;
    }
    case 'counterTill': {
      const w = d.w ?? 48;
      counter(c, { x: x - w / 2, y: y - 12, w, h: 12 }, '#8a5530', '#a36a40', '#6b3f22');
      block(c, x - 6, y - 26, 12, 9, '#2b3440');
      rect(c, '#57c278', x - 4, y - 24, 8, 3);
      return;
    }
    case 'drumKit':
      shadow(c, x, y, 12, 3);
      ellipse(c, OUTLINE, x, y - 8, 9, 7);
      ellipse(c, '#e2493b', x, y - 8, 8, 6);
      ellipse(c, '#f4f1ea', x, y - 9, 6, 4);
      ellipse(c, '#e2b33b', x - 10, y - 18, 5, 2);
      ellipse(c, '#e2b33b', x + 10, y - 16, 5, 2);
      return;
    default:
      return;
  }
}

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

function shelf(c: Ctx, r: Rect): void {
  bookshelf(c, r.x + r.w / 2, r.y + r.h, r.w, 0);
}

function bookshelf(c: Ctx, cx: number, by: number, w: number, variant: number): void {
  const h = variant === 2 ? 30 : 46;
  shadow(c, cx, by, w / 2 + 2, 3);
  block(c, cx - w / 2, by - h, w, h, '#5e381f', '#7a4a2a', '#4a2a16');
  rect(c, '#3a2214', cx - w / 2 + 2, by - h + 2, w - 4, h - 4);
  const cols = ['#c0392b', '#2f6db5', '#2e9a5a', '#e2b33b', '#8e44ad', '#d35400', '#ecf0f1'];
  const rows = Math.floor((h - 4) / 14);
  for (let row = 0; row < rows; row++) {
    const sy = by - h + 3 + row * 14;
    rect(c, '#7a4a2a', cx - w / 2 + 2, sy + 11, w - 4, 2);
    let bx = cx - w / 2 + 3, i = row * 3 + variant;
    while (bx < cx + w / 2 - 5) {
      const bw = 2 + (i % 3);
      const bh = 7 + ((i * 7) % 4);
      rect(c, cols[(i * 5 + row) % cols.length], bx, sy + 11 - bh, bw, bh);
      rect(c, 'rgba(255,255,255,0.3)', bx, sy + 11 - bh, 1, bh);
      bx += bw + 1;
      i++;
      if (i % 6 === 5) bx += 3;
    }
  }
}

function studentDesk(c: Ctx, cx: number, by: number, variant: number): void {
  shadow(c, cx, by, 14, 3);
  block(c, cx - 6, by - 4, 12, 6, '#3d5a80', '#4f6f98');
  block(c, cx - 14, by - 16, 28, 10, '#d9a876', '#ecc596', '#b8814e');
  rect(c, OUTLINE, cx - 12, by - 7, 2, 5);
  rect(c, OUTLINE, cx + 10, by - 7, 2, 5);
  if (variant === 1) return;
  if (variant === 2) {
    // A forgotten bag under this one.
    block(c, cx + 6, by - 6, 8, 6, '#e2493b');
    return;
  }
  block(c, cx - 8, by - 15, 9, 5, '#fffdf6');
}

function counter(c: Ctx, r: Rect, fill: string, light: string, dark: string): void {
  shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 1, 3);
  block(c, r.x, r.y - 10, r.w, r.h + 10, fill, light, dark);
  rect(c, shade(fill, -0.25), r.x + 1, r.y - 1, r.w - 2, 1);
}

function sofa(c: Ctx, r: Rect, base: string, light: string, dark: string, cushion: string): void {
  shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 3, 4);
  block(c, r.x, r.y - 14, r.w, 12, dark, base);
  block(c, r.x, r.y - 4, r.w, r.h + 4, base, light, dark);
  block(c, r.x - 3, r.y - 8, 7, r.h + 8, dark, base);
  block(c, r.x + r.w - 4, r.y - 8, 7, r.h + 8, dark, base);
  block(c, r.x + 5, r.y - 2, r.w / 2 - 7, 8, cushion);
  block(c, r.x + r.w / 2 + 2, r.y - 2, r.w / 2 - 7, 8, cushion);
  block(c, r.x + 6, r.y - 11, 9, 8, '#e07a5f', '#ee9a80');
}

function bench(c: Ctx, r: Rect): void {
  shadow(c, r.x + r.w / 2, r.y + r.h, r.w / 2 + 2, 3);
  rect(c, OUTLINE, r.x + 4, r.y - 2, 3, r.h + 2);
  rect(c, OUTLINE, r.x + r.w - 7, r.y - 2, 3, r.h + 2);
  block(c, r.x, r.y - 6, r.w, 5, '#b8814e', '#d9a876', '#8a5c34');
  block(c, r.x, r.y - 14, r.w, 4, '#b8814e', '#d9a876', '#8a5c34');
  rect(c, OUTLINE, r.x + 6, r.y - 11, 2, 6);
  rect(c, OUTLINE, r.x + r.w - 8, r.y - 11, 2, 6);
}

function berryBush(c: Ctx, r: Rect, t: number): void {
  bushShape(c, r.x + r.w / 2, r.y + r.h, t, '#3f8f48');
}

function bushShape(c: Ctx, cx: number, by: number, t: number, base: string): void {
  shadow(c, cx, by, 11, 3, 0.28, 14);
  const sway = wind.at(cx, by) * 0.8 + Math.sin(t * 1.4 + cx) * 0.3;
  const blobs: [number, number, number][] = [[-6, -7, 7], [6, -7, 7], [0, -12, 8], [0, -5, 8]];
  for (const [dx, dy, r] of blobs) ellipse(c, OUTLINE, cx + dx + sway, by + dy, r + 1, r * 0.8 + 1);
  for (const [dx, dy, r] of blobs) ellipse(c, base, cx + dx + sway, by + dy, r, r * 0.8);
  for (const [dx, dy, r] of blobs) ellipse(c, shade(base, 0.25), cx + dx + sway - 2, by + dy - 2, r * 0.5, r * 0.4);
  for (let i = 0; i < 4; i++) rect(c, '#e2493b', cx - 6 + i * 4 + sway, by - 11 + (i % 2) * 4, 2, 2);
}

export function pottedPlant(c: Ctx, cx: number, by: number, t: number): void {
  shadow(c, cx, by, 8, 2.5);
  block(c, cx - 6, by - 10, 12, 10, '#b0603a', '#c97a50', '#8a4426');
  rect(c, '#8a4426', cx - 7, by - 11, 14, 3);
  const sway = Math.sin(t * 1.3 + cx) * 0.4;
  const leaves: [number, number, number, number, string][] = [
    [-8, -20, 7, 9, '#2f7a3a'], [2, -22, 7, 10, '#2f7a3a'], [-4, -26, 9, 10, '#3f9a4a'],
    [-2, -31, 5, 8, '#5cb05e'], [-9, -16, 5, 5, '#3f9a4a'], [4, -16, 5, 5, '#5cb05e'],
  ];
  for (const [dx, dy, w, h] of leaves) rect(c, OUTLINE, cx + dx - 1 + sway, by + dy - 1, w + 2, h + 2);
  for (const [dx, dy, w, h, col] of leaves) rect(c, col, cx + dx + sway, by + dy, w, h);
}

export function tree(c: Ctx, cx: number, by: number, _t: number, kind: 'tree' | 'cherry' | 'pine', scale = 1): void {
  shadow(c, cx + 5, by - 1, 19 * scale, 5.5 * scale, 0.3, 50 * scale);
  drawTree(c, cx, by, kind, Math.floor(hash2(Math.round(cx), Math.round(by), 9) * 3), scale);
}

const OBJ: Partial<Record<ObjectId, Drawer>> = { ...OBJ_BASE, ...OBJ2, ...OBJ3, ...OBJ4, busStopW: OBJ_BASE.busStop, busStopE: OBJ_BASE.busStop };

/** Props whose drawing changes over time (never cached). */
const ANIMATED_OBJ = new Set<string>([...ANIMATED3_OBJ, ...ANIMATED4_OBJ, 'water', 'tv', 'vending', 'pool', 'gymNet', 'mallFountain', 'fountain', 'plant', 'picnic', 'field', 'pond', 'kiteTree', 'swing', 'bush1', 'bush2', 'bush3', 'tennisNet', 'volleyNet']);
const ANIMATED_DECOR = new Set<string>([...ANIMATED3_DECOR, ...ANIMATED4_DECOR, 'clothesRack', 'scoreboard', 'tree', 'cherry', 'pine', 'bushDeco', 'pottedPlant', 'fountain']);

let cacheScale = 2;
export function setPropScale(z: number): void {
  cacheScale = z;
}

/** Pre-render a static prop once and blit it afterwards (big win for furnished interiors). */
function cached(sp: PropSprite): PropSprite {
  let cv: HTMLCanvasElement | null = null;
  let scale = 0;
  const draw = sp.draw;
  return {
    ...sp,
    draw(c, t) {
      if (!cv || scale !== cacheScale) {
        scale = cacheScale;
        const w = sp.x1 - sp.x0, h = sp.bottom - sp.top + 8;
        cv = document.createElement('canvas');
        cv.width = Math.ceil(w * scale);
        cv.height = Math.ceil(h * scale);
        const cc = cv.getContext('2d')!;
        cc.scale(scale, scale);
        cc.translate(-sp.x0, -sp.top);
        draw(cc, t);
      }
      c.drawImage(cv, sp.x0, sp.top, sp.x1 - sp.x0, sp.bottom - sp.top + 8);
    },
  };
}

/** Build every depth-sorted prop for a map. */
export function buildProps(map: MapDef, extra: (id: ObjectId) => (() => boolean) | undefined = () => undefined): PropSprite[] {
  const props = buildPropsRaw(map, extra);
  return map.indoor ? props.map((p) => ((p as PropSprite & { animated?: boolean }).animated ? p : cached(p))) : props;
}

function buildPropsRaw(map: MapDef, extra: (id: ObjectId) => (() => boolean) | undefined): PropSprite[] {
  const props: PropSprite[] = OBJECTS.filter((o) => o.map === map.id).map((o) => ({
    sortY: o.solid ? o.rect.y + o.rect.h : o.rect.y,
    x0: o.rect.x - 40,
    x1: o.rect.x + o.rect.w + 40,
    top: o.rect.y - 80,
    bottom: o.rect.y + o.rect.h + 8,
    days: o.days,
    visible: o.fest ? () => festivalNow() === o.fest && (extra(o.id)?.() ?? true) : extra(o.id),
    animated: ANIMATED_OBJ.has(o.id) || ANIMATED_EXTRA.obj.has(o.id) || !!o.fest,
    draw: (c: Ctx, t: number) => (OBJ[o.id] ?? extraObjDrawer(o.id))?.(c, o.rect, t),
  }) as PropSprite);
  for (const d of map.decor) {
    if (d.wall) continue;
    props.push({
      sortY: d.kind === 'rugRound' ? -1 : OVERHEAD_DECOR.has(d.kind) || OVERHEAD4_DECOR.has(d.kind) ? d.y + 100000 : d.y,
      x0: d.x - (d.w ?? 64) / 2 - 40,
      x1: d.x + (d.w ?? 64) / 2 + 40,
      top: d.y - 90,
      bottom: d.y + 8,
      animated: ANIMATED_DECOR.has(d.kind) || ANIMATED_EXTRA.decor.has(d.kind) || !!d.fest,
      visible: d.fest ? () => festivalNow() === d.fest : undefined,
      draw: (c, t) => drawDecor(c, d, t),
    } as PropSprite);
  }
  return props;
}

export function mapHasProps(id: MapId): boolean {
  return OBJECTS.some((o) => o.map === id);
}
